

import { useEffect, useMemo, useRef } from 'react';
import { useInventoryDataStore } from '../lib/stores/inventory-data-store';
import { useShallow } from 'zustand/react/shallow';

export const useInventoryData = (autoLoad = true) => {
  // Track if we've already loaded data to prevent multiple calls
  const hasLoadedRef = useRef(false);

  // Use shallow selector for data and state values to prevent unnecessary re-renders
  const dataState = useInventoryDataStore(
    useShallow((state) => ({
      champignons: state.champignons,
      especes: state.especes,
      nuisibles: state.nuisibles,
      isLoadingChampignons: state.isLoadingChampignons,
      isLoadingEspeces: state.isLoadingEspeces,
      isLoadingNuisibles: state.isLoadingNuisibles,
      champignonsError: state.champignonsError,
      especesError: state.especesError,
      nuisiblesError: state.nuisiblesError,
    }))
  );

  // Get action functions separately (these are stable references from Zustand)
  const loadChampignons = useInventoryDataStore((state) => state.loadChampignons);
  const loadEspeces = useInventoryDataStore((state) => state.loadEspeces);
  const loadNuisibles = useInventoryDataStore((state) => state.loadNuisibles);
  const loadAllData = useInventoryDataStore((state) => state.loadAllData);
  const getChampignonById = useInventoryDataStore((state) => state.getChampignonById);
  const getEspeceById = useInventoryDataStore((state) => state.getEspeceById);
  const getNuisibleById = useInventoryDataStore((state) => state.getNuisibleById);
  const clearAllData = useInventoryDataStore((state) => state.clearAllData);

  // Auto-load data when component mounts (only once)
  useEffect(() => {
    if (autoLoad && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadAllData();
    }
  }, [autoLoad, loadAllData]);

  // Memoize computed values
  const isLoading = useMemo(
    () => dataState.isLoadingChampignons || dataState.isLoadingEspeces || dataState.isLoadingNuisibles,
    [dataState.isLoadingChampignons, dataState.isLoadingEspeces, dataState.isLoadingNuisibles]
  );

  const hasError = useMemo(
    () => !!dataState.champignonsError || !!dataState.especesError || !!dataState.nuisiblesError,
    [dataState.champignonsError, dataState.especesError, dataState.nuisiblesError]
  );

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // Data
      champignons: dataState.champignons,
      especes: dataState.especes,
      nuisibles: dataState.nuisibles,

      // Loading states
      isLoading,
      isLoadingChampignons: dataState.isLoadingChampignons,
      isLoadingEspeces: dataState.isLoadingEspeces,
      isLoadingNuisibles: dataState.isLoadingNuisibles,

      // Error states
      hasError,
      champignonsError: dataState.champignonsError,
      especesError: dataState.especesError,
      nuisiblesError: dataState.nuisiblesError,

      // Actions (stable references from Zustand)
      loadChampignons,
      loadEspeces,
      loadNuisibles,
      loadAllData,

      // Utility methods (stable references from Zustand)
      getChampignonById,
      getEspeceById,
      getNuisibleById,

      // Clear methods (stable references from Zustand)
      clearAllData,
    }),
    [
      dataState,
      isLoading,
      hasError,
      loadChampignons,
      loadEspeces,
      loadNuisibles,
      loadAllData,
      getChampignonById,
      getEspeceById,
      getNuisibleById,
      clearAllData,
    ]
  );
};
